import { cn } from "@/components/podkit/lib/cn";
import { TrackLocations } from "@/hooks/use-segment";
import { useSessionAgentExecution, useSessionEnvironment, useSessionName, useSessions } from "@/hooks/use-sessions";
import { getSessionUrl } from "./session/session-model";
import { type Session } from "./session/session-model";
import { type FC } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "@/components/flexkit/Link";
import { EnvironmentChanges } from "@/routes/environments/SidebarEnvironmentList";
import { IconBranch } from "@/assets/icons/geist/IconBranch";
import { useBranch } from "@/hooks/use-branch";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { AgentExecutionStatusIcon } from "@/components/ai/AgentExecutionStatusIcon";
import { Text } from "@/components/podkit/typography/Text";
import { SessionContextMenu } from "@/components/ai/session/SessionContextMenu";

export const SidebarSessionList: FC = () => {
    const { sessions } = useSessions();

    return (
        <div className="contents" data-testid="session-list" data-track-location={TrackLocations.SidebarSessionList}>
            {sessions.map((session) => (
                <SessionItem key={session.id} session={session} />
            ))}
        </div>
    );
};

const SessionItem: FC<{ session: Session }> = ({ session }) => {
    const location = useLocation();
    const url = getSessionUrl(session);
    const isActive = location.pathname === url;
    const name = useSessionName(session);
    const environment = useSessionEnvironment(session);
    const { activeAgentExecution } = useSessionAgentExecution(session);
    const branch = useBranch(environment);

    const clazzes = cn(
        "flex h-13 flex-row items-center rounded-lg pl-2",
        isActive ? "bg-surface-secondary" : "hover:bg-surface-03",
    );

    return (
        <SessionContextMenu session={session}>
            <Link id={session.id} className={clazzes} to={url} reloadIfFrontendUpdated={true}>
                <div className="flex w-full min-w-0 flex-col py-[6px] pl-1 pr-1">
                    <div className="flex flex-row items-center justify-between gap-1">
                        <div className={"flex-grow truncate text-start text-base font-bold"}>{name}</div>
                        {activeAgentExecution && (
                            <div className="flex flex-row items-center gap-1">
                                <AgentExecutionStatusIcon agentExecution={activeAgentExecution} size="sm" />
                            </div>
                        )}
                    </div>
                    {environment ? (
                        <div className="flex flex-row items-center gap-1">
                            <EnvironmentStatusDot env={environment} size="sm" className="m-0 size-4 shrink-0" />
                            <div className="flex min-w-0 items-center gap-0.5">
                                <IconBranch size="sm" className="size-3 shrink-0 text-content-primary" />
                                <span className="truncate text-sm text-content-primary" translate="no">
                                    {branch}
                                </span>
                            </div>
                            <EnvironmentChanges git={environment?.status?.content?.git} className="text-[10px]" />
                        </div>
                    ) : (
                        <div className="flex flex-row items-center gap-1">
                            <Text className="text-sm text-content-primary">No environment</Text>
                        </div>
                    )}
                </div>
            </Link>
        </SessionContextMenu>
    );
};
